import React from 'react';
import { motion } from 'motion/react';
import { Shield, Lock, EyeOff, Database, History, CheckCircle2, ShieldCheck, FileCheck } from 'lucide-react';

export default function DataProtectionPolicy() {
  const lastUpdated = "April 21, 2026";

  const protectionPrinciples = [
    {
      title: "Encryption in Transit",
      description: "All client data is encrypted during transmission using industry-standard HTTPS/TLS (1.3+) protocols, ensuring information remains private while moving through the network.",
      icon: Shield
    },
    {
      title: "Exposure Prevention",
      description: "Sensitive data is strictly segmented and never exposed in client-facing systems or logs. We utilize backend-only processing for all high-risk data operations.",
      icon: EyeOff
    },
    {
      title: "Restricted Access",
      description: "Data access is granted exclusively to authorized users based on verified business necessity and strictly follows our Access Control Policy.",
      icon: Lock
    },
    {
      title: "Smart Retention",
      description: "Data is retained only as long as necessary for business operations. We implement automated purging cycles for data that no longer serves its primary purpose.",
      icon: History
    },
    {
      title: "Integrity Checks",
      description: "We maintain data integrity through regular checksums and snapshot validation to prevent unauthorized or accidental modification of business records.",
      icon: Database
    },
    {
      title: "Disclosure Controls",
      description: "Agrandintl is committed to preventing unauthorized access or disclosure. We utilize strict non-disclosure legal frameworks alongside technical safeguards.",
      icon: FileCheck
    }
  ];

  return (
    <div className="min-h-screen bg-background pt-32 pb-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-4xl mx-auto space-y-12"
        >
          {/* Header */}
          <div className="space-y-4 text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/5 border border-accent/10 text-accent text-[10px] font-bold uppercase tracking-[0.3em]">
              Data Sovereignty & Safety
            </div>
            <h1 className="text-4xl md:text-6xl font-display font-bold tracking-tight">
              Data Protection <span className="text-accent">Policy</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Agrandintl ensures that all client data is handled securely, responsibly, and with absolute confidentiality.
            </p>
          </div>

          {/* Core Commitment Card */}
          <div className="glass-card p-8 md:p-12 rounded-[2.5rem] border-white/5 bg-white/[0.01] relative overflow-hidden text-center">
            <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 blur-[80px] rounded-full" />
            <div className="space-y-6 relative z-10">
              <div className="w-16 h-16 rounded-3xl bg-accent/10 flex items-center justify-center text-accent mx-auto mb-4">
                <ShieldCheck className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-display font-bold">Our Trust Commitment</h2>
              <p className="text-muted-foreground leading-relaxed text-base max-w-2xl mx-auto">
                "We are committed to maintaining data confidentiality and preventing unauthorized access or disclosure. Every layer of our technology stack is optimized for data safety."
              </p>
            </div>
          </div>

          {/* Principles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {protectionPrinciples.map((principle, idx) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-8 rounded-3xl border-white/5 bg-white/[0.01] hover:border-accent/20 transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-white/5 flex items-center justify-center text-accent shrink-0 group-hover:bg-accent group-hover:text-background transition-all">
                    <principle.icon className="w-5 h-5" />
                  </div>
                  <div className="space-y-2">
                    <h3 className="text-lg font-display font-bold">{principle.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Compliance & Governance */}
          <div className="pt-12 border-t border-white/5 text-center space-y-6">
            <p className="text-muted-foreground text-sm max-w-xl mx-auto italic">
              Our Data Protection Policy is an evolving document, updated regularly to address emerging technical challenges and global compliance requirements.
            </p>
            <div className="flex justify-center flex-wrap gap-4 pt-4">
              {["TLS 1.3", "AES-256", "HTTPS ENFORCED"].map((tag) => (
                <div key={tag} className="px-4 py-1.5 rounded-full border border-white/5 bg-white/[0.02] text-[9px] font-bold tracking-widest text-muted-foreground uppercase">
                  {tag}
                </div>
              ))}
            </div>
            <div className="pt-8 text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground/60">
              Document Ref: AG-DP-01 | Last Updated: {lastUpdated}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
